import { useCallback, useMemo, useState } from 'react'
import { Button, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import type { SqlLanguage } from 'sql-formatter'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'sql-formatter'

type SqlFormatterResult = { ok: true; result: string } | { ok: false; error: string }
type SqlDialectLabelKey =
  | 'sql'
  | 'postgresql'
  | 'mysql'
  | 'sqlite'
  | 'mariadb'
  | 'transactsql'
  | 'bigquery'
  | 'snowflake'

interface DialectOption {
  value: SqlLanguage
  labelKey: SqlDialectLabelKey
}

const DIALECT_OPTIONS: DialectOption[] = [
  { value: 'sql', labelKey: 'sql' },
  { value: 'postgresql', labelKey: 'postgresql' },
  { value: 'mysql', labelKey: 'mysql' },
  { value: 'sqlite', labelKey: 'sqlite' },
  { value: 'mariadb', labelKey: 'mariadb' },
  { value: 'transactsql', labelKey: 'transactsql' },
  { value: 'bigquery', labelKey: 'bigquery' },
  { value: 'snowflake', labelKey: 'snowflake' },
]

async function formatSql(input: string, dialect: SqlLanguage): Promise<SqlFormatterResult> {
  if (!input.trim()) return { ok: false, error: 'empty' }

  try {
    const { format } = await import('sql-formatter')
    const result = format(input, {
      language: dialect,
      keywordCase: 'upper',
      dataTypeCase: 'upper',
      functionCase: 'upper',
      tabWidth: 2,
      linesBetweenQueries: 1,
    })

    return { ok: true, result: result.trimEnd() }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return { ok: false, error: message }
  }
}

export function SqlFormatterPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [dialect, setDialect] = useState<SqlLanguage>('sql')
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [copied, setCopied] = useState(false)

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    return { chars, lines }
  }, [output])

  const handleFormat = useCallback(async () => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setStatusMessage(null)
      return
    }

    const start = performance.now()
    setIsProcessing(true)
    setError(null)
    setStatusMessage(null)
    toolInvoke(UTILITY, { dialect })

    const result = await formatSql(input, dialect)

    if (result.ok) {
      setOutput(result.result)
      setStatusMessage(t.sqlFormatter.formatted)
      toolSuccess(UTILITY, Math.round(performance.now() - start), { dialect })
    } else {
      setOutput('')
      setError(t.sqlFormatter.errors.invalidSql.replace('{error}', result.error))
      toolError(UTILITY, 'invalid-sql', { dialect })
    }

    setIsProcessing(false)
  }, [dialect, input, t])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setStatusMessage(null)
  }, [])

  return (
    <ToolPageShell toolKey="sqlFormatter" width="wide">
      <CodeWorkspace
        error={error}
        notice={statusMessage}
        controls={(
          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-sm font-medium tracking-normal text-foreground">
              <span>{t.sqlFormatter.dialect}</span>
              <select
                value={dialect}
                onChange={(event) => {
                  setDialect(event.target.value as SqlLanguage)
                  setError(null)
                  setStatusMessage(null)
                }}
                className={cn(
                  'h-10 min-w-44 rounded-md border border-input bg-background px-3 py-2',
                  'font-mono text-sm text-foreground shadow-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary/35',
                )}
                aria-label={t.sqlFormatter.dialect}
              >
                {DIALECT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t.sqlFormatter.dialects[option.labelKey]}
                  </option>
                ))}
              </select>
            </label>
            <Button onClick={() => void handleFormat()} disabled={!input.trim() || isProcessing}>
              {isProcessing ? t.sqlFormatter.formatting : t.sqlFormatter.format}
            </Button>
          </div>
        )}
        input={{
          title: t.sqlFormatter.input,
          value: input,
          onChange: (value) => {
            setInput(value)
            setError(null)
            setStatusMessage(null)
          },
          placeholder: t.sqlFormatter.inputPlaceholder,
          ariaLabel: t.sqlFormatter.input,
          clear: { label: t.sqlFormatter.clear, onClick: handleClear },
          stats: [
            {
              label: t.sqlFormatter.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.sqlFormatter.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.sqlFormatter.output,
          value: output,
          readOnly: true,
          placeholder: t.sqlFormatter.outputPlaceholder,
          ariaLabel: t.sqlFormatter.output,
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.sqlFormatter.copied : t.sqlFormatter.copy}
            </Button>
          ),
          stats: output && !error
            ? [
              {
                label: t.sqlFormatter.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.sqlFormatter.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.sqlFormatter.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.sqlFormatter.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.sqlFormatter.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.sqlFormatter.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
